/**
 * The viewer's side of reactions in a live (`prototype/live-sessions.html`,
 * screens 2 and 3): the seven buttons, a glass pill on the picture on a
 * desktop and a row of thumb-sized buttons under it on a phone, and what a
 * tap does while live.
 */
import { type ReactNode, type RefObject, useCallback, useRef, useState } from "react";
import { freshBucket, REACTIONS, type ReactionCounts, type ReactionKind, takeTap } from "../../shared/live-reactions";
import type { RoomChatSocket } from "../live/room/chat";
import type { Lane } from "../live/room/lane";

export function ReactionButtons({
  className,
  onTap,
  labels,
}: {
  className: string;
  onTap: (kind: ReactionKind) => void;
  /** The label over a button on hover, where there is a pointer to hover with. */
  labels: boolean;
}): ReactNode {
  return (
    <div className={className} role="group" aria-label="React">
      {REACTIONS.map((r) => (
        <button
          key={r.kind}
          type="button"
          className="w-rx-b"
          aria-label={r.label}
          onClick={(e) => {
            const b = e.currentTarget;
            b.classList.remove("w-pop");
            // Why read the width: it restarts the pop when the same button is tapped again mid-animation.
            void b.offsetWidth;
            b.classList.add("w-pop");
            onTap(r.kind);
          }}
        >
          <span aria-hidden="true">{r.emoji}</span>
          {labels ? (
            <span className="w-rx-tip" aria-hidden="true">
              {r.label}
            </span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export interface LiveReactions {
  lane: RefObject<Lane | null>;
  tap: (kind: ReactionKind) => void;
  /** The room's totals for the second just gone, without this page's own taps. */
  heard: (counts: ReactionCounts) => void;
}

/**
 * While live: a tap floats here at once and goes to the room as one
 * `react`, within the limit; a tap past it floats and is not sent. Everyone
 * else's come back once a second as the room's totals: the room leaves this
 * page's own out, since they already floated.
 */
export function useLiveReactions(socket: Pick<RoomChatSocket, "send"> | null, perSec: number): LiveReactions {
  const lane = useRef<Lane | null>(null);
  const [bucket] = useState(() => freshBucket(Date.now()));
  const tap = useCallback(
    (kind: ReactionKind) => {
      lane.current?.mine(kind);
      if (takeTap(bucket, Date.now())) socket?.send({ type: "react", kind });
    },
    [socket, bucket],
  );
  const heard = useCallback((counts: ReactionCounts) => lane.current?.second(counts, perSec), [perSec]);
  return { lane, tap, heard };
}
